import { Card, Typography, Radio, InputNumber, Table, Button } from "antd";
import { useState, useMemo, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const { Title } = Typography;

export default function ViewEmpSalary({ amount, cid }) {
    const { backUrl, authData } = useContext(AuthContext);
    const [salaryType, setSalaryType] = useState("CTC");
    const [totalAmount, setTotalAmount] = useState(amount || 0);
    const [components, setComponents] = useState({
        basic: 0,
        hra: 0,
        conv: 0,
        allow: 0,
        pf_er: 0,
        esi_er: 0,
        pf_ee: 0,
        esi_ee: 0,
    });
    const [isUserEditing, setIsUserEditing] = useState(false);

    // Sync when `amount` changes from parent
    useEffect(() => {
        if (amount && !isUserEditing) {
            setTotalAmount(amount);
            const initialComponents = calculateInitialComponents(amount, salaryType);
            setComponents(initialComponents);
        }
    }, [amount, salaryType]);

    // Calculate initial component values based on total amount and salary type
    const calculateInitialComponents = (total, type) => {
        let basic = 0,
            hra = 0,
            conv = 0,
            allow = 0,
            pf_er = 0,
            esi_er = 0,
            pf_ee = 0,
            esi_ee = 0,
            gross = 0;

        if (total > 0) {
            if (type === "CTC") {
                basic = Math.round(total * 0.5);
                hra = Math.round(total * 0.3);
                conv = Math.round(total * 0.1);
                allow = Math.round(total * 0.1);

                const pf_base = basic + conv + allow;
                const pf_wage = Math.min(pf_base, 15000);
                pf_er = Math.round((pf_wage * 12) / 100);
                pf_ee = pf_er;

                esi_er = total <= 21000 ? Math.round((total * 3.25) / 100) : 0;
                gross = total - pf_er - esi_er;

                basic = Math.round(gross * 0.5);
                hra = Math.round(gross * 0.3);
                conv = Math.round(gross * 0.1);
                allow = gross - basic - hra - conv;

                esi_ee = gross <= 21000 ? Math.round((gross * 0.75) / 100) : 0;
            } else if (type === "Gross") {
                basic = Math.round(total * 0.5);
                hra = Math.round(total * 0.3);
                conv = Math.round(total * 0.1);
                allow = Math.round(total * 0.1);

                const pf_base = basic + conv + allow;
                const pf_wage = Math.min(pf_base, 15000);
                pf_er = Math.round((pf_wage * 12) / 100);
                pf_ee = pf_er;

                esi_er = 0;
                gross = total - pf_er - esi_er;

                basic = Math.round(gross * 0.5);
                hra = Math.round(gross * 0.3);
                conv = Math.round(gross * 0.1);
                allow = gross - basic - hra - conv;

                esi_ee = 0;
            } else {
                // Net
                gross = total;
                basic = Math.round(total * 0.5);
                hra = Math.round(total * 0.3);
                conv = Math.round(total * 0.1);
                allow = total - basic - hra - conv;

                pf_er = 0;
                pf_ee = 0;
                esi_er = 0;
                esi_ee = 0;
            }
        }

        return { basic, hra, conv, allow, pf_er, esi_er, pf_ee, esi_ee };
    };

    // Update totalAmount when components or salaryType change
    useEffect(() => {
        if (isUserEditing) {
            const { basic, hra, conv, allow, pf_er, esi_er, pf_ee, esi_ee } = components;
            let newTotal;
            if (salaryType === "CTC") {
                newTotal = basic + hra + conv + allow + pf_er + esi_er;
            } else if (salaryType === "Gross") {
                newTotal = basic + hra + conv + allow;
            } else {
                const gross = basic + hra + conv + allow;
                newTotal = gross - pf_ee - esi_ee;
            }
            setTotalAmount(newTotal);
        }
    }, [components, salaryType, isUserEditing]);

    // Computed gross and net
    const { gross, net } = useMemo(() => {
        const { basic, hra, conv, allow, pf_er, esi_er, pf_ee, esi_ee } = components;
        let gross = basic + hra + conv + allow;
        let net = gross - pf_ee - esi_ee;

        if (salaryType === "CTC" && !isUserEditing) {
            gross = totalAmount - pf_er - esi_er;
            net = gross - pf_ee - esi_ee;
        }

        return { gross, net };
    }, [components, salaryType, totalAmount, isUserEditing]);

    // Handle component input changes
    const handleComponentChange = (value, component) => {
        setIsUserEditing(true);
        setComponents((prev) => {
            const updated = { ...prev, [component]: value || 0 };
            if (salaryType === "Gross" || salaryType === "Net") {
                updated.pf_er = 0;
                updated.pf_ee = 0;
                updated.esi_er = 0;
                updated.esi_ee = 0;
            }
            return updated;
        });
    };

    // Handle edit button click
    const handleEditSubmit = async () => {
        const deductions_pm = components.pf_ee + components.esi_ee;
        const deductions_py = deductions_pm * 12;
        const salaryData = {
            basic_pm: components.basic || null,
            hra_pm: components.hra || null,
            conveyance_pm: components.conv || null,
            other_allowance_pm: components.allow || null,
            gross_pm: gross || null,
            pf_employer_pm: components.pf_er || null,
            esi_employer_pm: components.esi_er || null,
            ctc_pm: salaryType === "CTC" ? totalAmount || null : null,
            pf_employee_pm: components.pf_ee || null,
            esi_employee_pm: components.esi_ee || null,
            deductions_pm: deductions_pm || null,
            net_pay_pm: net || null,
            basic_py: components.basic ? components.basic * 12 : null,
            hra_py: components.hra ? components.hra * 12 : null,
            conveyance_py: components.conv ? components.conv * 12 : null,
            other_allowance_py: components.allow ? components.allow * 12 : null,
            gross_py: gross ? gross * 12 : null,
            pf_employer_py: components.pf_er ? components.pf_er * 12 : null,
            esi_employer_py: components.esi_er ? components.esi_er * 12 : null,
            ctc_py: salaryType === "CTC" ? (totalAmount ? totalAmount * 12 : null) : null,
            pf_employee_py: components.pf_ee ? components.pf_ee * 12 : null,
            esi_employee_py: components.esi_ee ? components.esi_ee * 12 : null,
            deductions_py: deductions_pm ? deductions_py : null,
            net_pay_py: net ? net * 12 : null,
            offered_salary: totalAmount,
            salary_type: salaryType
        };

        console.log("Salary Data:", salaryData);

        try {
            await axios.patch(`${backUrl}/api/emp/emp-update-salary/${cid}/`, salaryData, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            alert("Salary data sent to backend successfully");
        } catch (error) {
            console.error("Error sending salary data to backend:", error);
            alert("Failed to send salary data to backend");
        }
    };

    // Table rows
    const dataSource = [
        { key: "1", component: "Basic", monthly: components.basic, yearly: components.basic * 12 },
        { key: "2", component: "HRA", monthly: components.hra, yearly: components.hra * 12 },
        { key: "3", component: "Conveyance", monthly: components.conv, yearly: components.conv * 12 },
        { key: "4", component: "Allowances", monthly: components.allow, yearly: components.allow * 12 },
        { key: "5", component: "Gross Wages", monthly: gross, yearly: gross * 12 },
        { key: "6", component: "CTC", monthly: totalAmount, yearly: totalAmount * 12 },
        { key: "7", component: "PF (Employer)", monthly: components.pf_er, yearly: components.pf_er * 12 },
        { key: "8", component: "PF (Employee)", monthly: components.pf_ee, yearly: components.pf_ee * 12 },
        { key: "9", component: "ESI (Employer)", monthly: components.esi_er, yearly: components.esi_er * 12 },
        { key: "10", component: "ESI (Employee)", monthly: components.esi_ee, yearly: components.esi_ee * 12 },
        { key: "11", component: "Deductions", monthly: components.pf_ee + components.esi_ee, yearly: (components.pf_ee + components.esi_ee) * 12 },
        { key: "12", component: "Net Take Home", monthly: net, yearly: net * 12 },
    ];

    const columns = [
        {
            title: "Component",
            dataIndex: "component",
            key: "component",
            width: "30%",
            render: (text) => <b>{text}</b>,
        },
        {
            title: "Monthly (₹)",
            dataIndex: "monthly",
            key: "monthly",
            align: "center",
            width: "30%",
            render: (val, record) => (
                <InputNumber
                    value={val ?? 0}   // default to 0
                    min={0}
                    onChange={(value) =>
                        handleComponentChange(
                            value ?? 0,
                            record.component
                                .toLowerCase()
                                .replace(" ", "_")
                                .replace("gross_wages", "gross")
                                .replace("net_take_home", "net")
                                .replace("deductions", "deductions")
                                .replace("ctc", "ctc")
                                .replace(" (employer)", "_er")
                                .replace(" (employee)", "_ee")
                        )
                    }
                    formatter={(value) => (value > 0 ? value.toLocaleString("en-IN") : "0")}
                    parser={(value) => value.replace(/\D/g, "")}
                    style={{ width: "50%" }}
                />
            ),
        },
        {
            title: "Yearly (₹)",
            dataIndex: "yearly",
            key: "yearly",
            align: "center",
            width: "30%",
            render: (val) => (val && val > 0 ? val.toLocaleString("en-IN") : "0"),
        },
    ];



    return (
        <Card className="p-4 shadow-md rounded-xl">
            {/* <Title level={4} style={{ textAlign: "center", marginBottom: "16px" }}>
                Salary Calculator
            </Title> */}

            <Radio.Group
                value={salaryType}
                onChange={(e) => {
                    setSalaryType(e.target.value);
                    setIsUserEditing(false);
                    const initialComponents = calculateInitialComponents(totalAmount, e.target.value);
                    setComponents(initialComponents);
                }}
                style={{ width: "100%", display: "flex", justifyContent: "center", marginBottom: 16 }}
                buttonStyle="solid"
            >
                <Radio.Button value="CTC">CTC</Radio.Button>
                <Radio.Button value="Gross">Gross</Radio.Button>
                <Radio.Button value="Net">Net</Radio.Button>
            </Radio.Group>

            <center>
                <label>Enter Total {salaryType} Amount:</label>
                <InputNumber
                    style={{ width: "30%", marginTop: "8px", marginBottom: "16px" }}
                    value={totalAmount}
                    onChange={(value) => {
                        setIsUserEditing(false);
                        setTotalAmount(value || 0);
                        const initialComponents = calculateInitialComponents(value || 0, salaryType);
                        setComponents(initialComponents);
                    }}
                    min={0}
                    formatter={(value) => (value > 0 ? value.toLocaleString("en-IN") : "")}
                    parser={(value) => value.replace(/\D/g, "")}
                    placeholder={`Enter total ${salaryType} amount`}
                />
            </center>


            {totalAmount > 0 && (
                <>
                    <Table
                        dataSource={dataSource}
                        columns={columns}
                        pagination={false}
                        bordered
                        // size="middle"
                        rowClassName={(record) =>
                            record.component === "Net Take Home"
                                ? "ant-table-row-selected"
                                : record.component === "Gross Wages"
                                    ? "ant-table-row-hover"
                                    : ""
                        }
                        components={{
                            header: {
                                cell: (props) => (
                                    <th {...props} style={{ backgroundColor: "#0e497b", color: 'white', marginTop: '20px' }}>
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                    />
                    <div style={{ textAlign: "center", marginTop: "16px" }}>
                        <Button type="primary" onClick={handleEditSubmit}>
                            Submit
                        </Button>
                    </div>
                </>
            )}
        </Card>
    );
}
